import { validate as emailValidator } from 'email-validator'
import { parseDate, yearsAgo } from '../utils/helpers'
import {
  State,
  RUSSIAN_PASSPORT,
  profileType_Fiscal,
} from '../state-actions-reducers'
import { Country } from '../types/common-types'
import { validateStepPhysicalRus } from './Physical/validateStepPhysicalRus'
import { validateStepPhysicalKzt } from './Physical/validateStepPhysicalKzt'
import { validateStepCompanyRus } from './Fiscal/Company/validateStepCompanyRus'
import { validateStepCompanyKzt } from './Fiscal/Company/validateStepCompanyKzt'
import { validateStepCompanyKgz } from './Fiscal/Company/validateStepCompanyKgz'
import { validateStepIndividualRus } from './Fiscal/Individual/validateStepIndividualRus'
import { validateStepIndividualKzt } from './Fiscal/Individual/validateStepIndividualKzt'
import { validateStepIndividualKgz } from './Fiscal/Individual/validateStepIndividualKgz'

interface VlidateDateArgs {
  current: Date | string
  min?: Date
  max?: Date
  required?: boolean
}

export function validateDate({
  current,
  min,
  max,
  required,
}: VlidateDateArgs): boolean {
  // By default allow empty strings. Make stronger checking on submitting
  const currentValue = parseDate(current)
  if (!currentValue) {
    return !required
  }
  if (min && max) {
    return min <= currentValue && currentValue <= max
  }
  if (min) {
    return min <= currentValue
  }
  if (max) {
    return currentValue <= max
  }
  return true
}
export function validateTg(string: string): boolean {
  const trimmedString = (string??'').trim()
  const letter = trimmedString.match(/[a-zA-Zа-яёА-ЯЁ]/g)?.length ?? 0
  return letter >= 4
}

export function validateDatesRange(from: Date | string, to: Date | string) {
  const smallerDate = parseDate(from)
  const biggerDate = parseDate(to)
  if (!smallerDate || !biggerDate) {
    return false
  }
  return smallerDate < biggerDate
}

export function validateCollegeDates(
  startDate: string | Date,
  endDate: string | Date,
  required = false
) {
  if (!startDate || !endDate) return !required
  // Check if dates is valid
  return validateDatesRange(startDate, endDate)
}

// By default allow empty strings
// Will make stronger validation on submitting
export function validateString(
  string: string,
  maxLength: number,
  required?: boolean,
  regexp?: RegExp
): boolean {
  const minLength = required ? 1 : 0
  const trimmedString = (string??'').trim()
  return (
    trimmedString.length >= minLength &&
    trimmedString.length <= maxLength &&
    Boolean(trimmedString.match(regexp || ''))
  )
}

export function validateAddress(name: string, required?: boolean): boolean {
  return validateString(name, 200, required)
}

export function validateBankAccount(name: string, required?: boolean): boolean {
  return validateString(name, 200, required)
}

export function validateName(name: string, required?: boolean): boolean {
  return validateString(name, 200, required)
}

export function birthPlace(place: string, required?: boolean): boolean {
  return validateString(place, 300, required)
}

export function stringDigitsLength(
  digits: string,
  digitslength: number,
  required = true
): boolean {
  return (
    validateString(digits, 100, false) &&
    ((required && digits.length == digitslength) || !required)
  )
}

export function passportDigits(digits: string, required?: boolean): boolean {
  return validateString(digits, 100, required)
}

export function passportText(text: string, required?: boolean): boolean {
  return validateString(text, 400, required)
}

export function validateEmail(text: string, required?: boolean): boolean {
  if (!text && !required) return true
  return validateString(text, 100, required) && emailValidator(text)
}

export function validateReferences(
  values: string[],
  required = false
): boolean {
  if (!required && values.length === 0) return true
  if (required && values.length === 0) return false
  if (values.length < 20) {
    return values.every(value => {
      return validateString(value, 200)
    })
  }
  return false
}

const URL_REGEXP = /^((http(s){0,1}:\/\/(\w|\d)+){1}|\w+(\w|\d)*\.\w)[^\s]*((\w|\d)\.(\w|\d)[^\s]*)*(\w|\d|\/)$/g
export function validateUrl(
  url: string,
  maxLength = 300,
  required = false
): boolean {
  if (!url && !required) return true
  if (url) {
    if (typeof url === 'string') {
      if (url.length < maxLength) {
        return Boolean(url.match(URL_REGEXP))
      }
    }
  }
  return false
}

export function validateSocialLink(value: string, required = false): boolean {
  return validateUrl(value, 300, required)
  return false
}

export function validatePhone(value: string): boolean {
  return validateString(value, 24, true) && value.length >= 10
}

export function validatePassportNumber(
  value: string,
  isRussian = false,
  required = false,
  minLength?: number,
  maxLength?: number
) {
  if (isRussian) {
    return (
      validateString(value, maxLength || 7, required) &&
      value.replace(/[^0-9]/g, '').length >= (minLength || 3)
    )
  }
  return validateString(value, 200, required)
}

export function smartValidateString(string: string): boolean {
  const trimmedString = (string??'').trim()
  const letter = trimmedString.match(/[a-zA-Zа-яёА-ЯЁ]/g)?.length ?? 0
  const nums = trimmedString.match(/[0-9]/g)?.length ?? 0
  const symbols =
    trimmedString.match(
      /[\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\']/g
    )?.length ?? 0
  return (
    /[a-zA-Zа-яёА-ЯЁ]/g.test(trimmedString) &&
    symbols <= letter * 2 &&
    nums <= letter * 4
  )
}

export function validateStep(step: number, state: State): string[] {
  if (state.profileType == profileType_Fiscal) {
    if (state.F_FiscalType === '1') {
      return (
        // Казахстан
        state.countryRegistration === '398'
          ? validateStepIndividualKzt(step, state)
          : // Киргизия
          state.countryRegistration === '417' ||
            state.countryRegistration === '642'
          ? validateStepIndividualKgz(step, state)
          : validateStepIndividualRus(step, state)
      )
    } else {
      return (
        // Казахстан
        state.countryRegistration === '398'
          ? validateStepCompanyKzt(step, state)
          : // Киргизия
          state.countryRegistration === '417' ||
            state.countryRegistration === '642'
          ? validateStepCompanyKgz(step, state)
          : validateStepCompanyRus(step, state)
      )
    }
  } else {
    console.log('state.iframeCountry', state.iframeCountry)
    return state.iframeCountry === 'kz'
      ? validateStepPhysicalKzt(step, state)
      : validateStepPhysicalRus(step, state)
  }
}

export function validateForm(state: State): string[] {
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].reduce(
    (result: string[], step: number): string[] => [
      ...result,
      ...validateStep(step, state),
    ],
    []
  )
}

export function trimForm(state: State): State {
  return Object.keys(state).reduce((trimmedState: State, key) => {
    switch (key) {
      case 'mobilePhoneNumbers':
      case 'F_WorkPhoneNumber':
      case 'contactPhones':
      case 'addressRegisteredPhones':
      case 'addressFactualPhones':
      case 'workPhoneNumbers':
        // Filter out invalid values
        return {
          ...trimmedState,
          [key]: state[key].filter(validatePhone),
        }
      case 'emails':
        return {
          ...trimmedState,
          emails: state.emails.filter(email => validateEmail(email, true)),
        }
      case 'F_Email':
        return {
          ...trimmedState,
          emails: state.emails.filter(email => validateEmail(email, true)),
        }
      case 'vk':
      case 'facebook':
      case 'youtube':
      case 'vimeo':
      case 'instagram':
      case 'workWebsite':
      case 'personalWebsite':
      case 'additionalLinks':
      case 'addressRegisteredDate':
        return {
          ...trimmedState,
          addressRegisteredDate: validateDate({
            current: state.addressRegisteredDate,
            max: new Date(),
          })
            ? state.addressRegisteredDate
            : '',
        }
      case 'isStudent':
        return {
          ...trimmedState,
          collegeStartedAt: state.isStudent ? state.collegeStartedAt : '',
          collegeEndedAt: state.isStudent ? state.collegeEndedAt : '',
        }
      default:
        return trimmedState
    }
  }, state)
}
